import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, CalendarClock } from "lucide-react";
import { Button } from "./ui/button";

const STATUS_STYLES = {
  completed: { chip: "bg-green-100 text-green-700 border-green-200", dot: "bg-green-500", label: "Completed" },
  running: { chip: "bg-blue-100 text-blue-700 border-blue-200", dot: "bg-blue-500", label: "Running" },
  paused: { chip: "bg-amber-100 text-amber-700 border-amber-200", dot: "bg-amber-500", label: "Paused" },
  scheduled: { chip: "bg-purple-100 text-purple-700 border-purple-200", dot: "bg-purple-500", label: "Scheduled" },
  draft: { chip: "bg-slate-100 text-slate-600 border-slate-200", dot: "bg-slate-400", label: "Draft" },
};

const styleFor = (status) => STATUS_STYLES[status] || STATUS_STYLES.draft;

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Resolve the date a campaign should appear on in the calendar.
const campaignDate = (c) => {
  const raw =
    c.status === "scheduled"
      ? c.scheduled_at
      : c.started_at || c.scheduled_at || c.created_at;
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

export const CampaignCalendar = ({ campaigns = [] }) => {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedKey, setSelectedKey] = useState(dayKey(today));

  // Group campaigns by day key.
  const byDay = useMemo(() => {
    const map = {};
    campaigns.forEach((c) => {
      const d = campaignDate(c);
      if (!d) return;
      const k = dayKey(d);
      (map[k] = map[k] || []).push(c);
    });
    return map;
  }, [campaigns]);

  // Build the 6-week grid for the current month.
  const grid = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = new Date(first);
    start.setDate(first.getDate() - first.getDay());
    const days = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      days.push(d);
    }
    return days;
  }, [cursor]);

  const monthLabel = cursor.toLocaleString("en-US", { month: "long", year: "numeric" });
  const selectedDate = grid.find((d) => dayKey(d) === selectedKey) || null;
  const selectedCampaigns = byDay[selectedKey] || [];

  const goPrev = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const goNext = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  const goToday = () => {
    setCursor(new Date(today.getFullYear(), today.getMonth(), 1));
    setSelectedKey(dayKey(today));
  };

  return (
    <div className="p-4" data-testid="campaign-calendar">
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={goPrev}
            data-testid="calendar-prev-month"
          >
            <ChevronLeft size={16} />
          </Button>
          <h3 className="font-heading font-semibold text-lg text-slate-900 w-44 text-center" data-testid="calendar-month-label">
            {monthLabel}
          </h3>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={goNext}
            data-testid="calendar-next-month"
          >
            <ChevronRight size={16} />
          </Button>
        </div>
        <Button variant="outline" size="sm" onClick={goToday} data-testid="calendar-today-btn">
          Today
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Calendar grid */}
        <div className="lg:col-span-2">
          <div className="grid grid-cols-7 mb-1">
            {WEEKDAYS.map((w) => (
              <div key={w} className="text-center text-xs font-medium text-slate-400 py-1">
                {w}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {grid.map((d) => {
              const k = dayKey(d);
              const inMonth = d.getMonth() === cursor.getMonth();
              const isToday = isSameDay(d, today);
              const isSelected = k === selectedKey;
              const items = byDay[k] || [];
              return (
                <button
                  key={k}
                  onClick={() => setSelectedKey(k)}
                  data-testid={`calendar-day-${k}`}
                  className={`min-h-[76px] rounded-md border p-1.5 text-left transition-colors flex flex-col ${
                    isSelected
                      ? "border-electric-blue ring-1 ring-electric-blue bg-blue-50/40"
                      : "border-slate-200 hover:bg-slate-50"
                  } ${inMonth ? "bg-white" : "bg-slate-50/60"}`}
                >
                  <span
                    className={`text-xs font-medium mb-1 inline-flex items-center justify-center h-5 w-5 rounded-full ${
                      isToday ? "bg-electric-blue text-white" : inMonth ? "text-slate-700" : "text-slate-400"
                    }`}
                  >
                    {d.getDate()}
                  </span>
                  <div className="space-y-0.5 overflow-hidden">
                    {items.slice(0, 2).map((c) => {
                      const s = styleFor(c.status);
                      return (
                        <div
                          key={c.campaign_id}
                          className={`truncate rounded px-1 py-0.5 text-[10px] font-medium border ${s.chip}`}
                          title={c.name || c.subject}
                        >
                          {c.name || c.subject || "Untitled"}
                        </div>
                      );
                    })}
                    {items.length > 2 && (
                      <div className="text-[10px] text-slate-500 px-1">+{items.length - 2} more</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-3 mt-4">
            {Object.entries(STATUS_STYLES).map(([key, s]) => (
              <div key={key} className="flex items-center gap-1.5">
                <span className={`h-2.5 w-2.5 rounded-full ${s.dot}`} />
                <span className="text-xs text-slate-500">{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Selected day panel */}
        <div className="lg:col-span-1">
          <div className="border border-slate-200 rounded-md h-full">
            <div className="p-3 border-b border-slate-200">
              <p className="text-sm font-semibold text-slate-900" data-testid="calendar-selected-date">
                {selectedDate
                  ? selectedDate.toLocaleDateString("en-US", {
                      weekday: "long",
                      month: "long",
                      day: "numeric",
                    })
                  : "Select a day"}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                {selectedCampaigns.length} campaign{selectedCampaigns.length === 1 ? "" : "s"}
              </p>
            </div>
            <div className="p-3 space-y-2 max-h-[360px] overflow-y-auto" data-testid="calendar-day-campaigns">
              {selectedCampaigns.length > 0 ? (
                selectedCampaigns.map((c, i) => {
                  const s = styleFor(c.status);
                  const d = campaignDate(c);
                  return (
                    <motion.div
                      key={c.campaign_id}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.04 }}
                      className="rounded-md border border-slate-200 p-2.5"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {c.name || c.subject || "Untitled"}
                        </p>
                        <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium border ${s.chip}`}>
                          {s.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                        <span>{c.total_emails || 0} recipients</span>
                        {c.status === "scheduled" && d && (
                          <span className="flex items-center gap-1 text-purple-600">
                            <CalendarClock size={11} />
                            {d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </span>
                        )}
                      </div>
                    </motion.div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-sm text-slate-400" data-testid="calendar-day-empty">
                  No campaigns on this day
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignCalendar;
